import React, { FC, useEffect, useState } from 'react';
import styles from '../journey-explorer.module.scss';
import { JourneyFilterState, JourneyPath } from '../../../../models/journey-explorer/journey-explorer';
import { formatNumber, formatSpan } from '../journey-explorer-utils';
import { InteractionInfo } from '../journey-map-layout';
import CustomerDrilldown from './customer-drilldown';
import PathChips from './path-chips';
import { CompareData, formatPct, pathPair } from '../journey-compare';

const NO_PATHS = 'No paths match these filters. Remove a filter from the bar above to widen the customer set.';

interface JourneyPathsProps {
	paths: JourneyPath[] | null;
	filters: JourneyFilterState | null;
	lookup: Map<string, InteractionInfo>;
	loading: boolean;
	onOpenCustomer: (customerId: string) => void;
	compare: CompareData | null;
}

const JourneyPaths: FC<JourneyPathsProps> = ({ paths, filters, lookup, loading, onOpenCustomer, compare }) => {
	const [open, setOpen] = useState(false);
	const [expanded, setExpanded] = useState<string | null>(null);

	useEffect(() => setExpanded(null), [paths]);

	const count = paths?.length ?? 0;

	return (
		<section className={styles.paths_panel}>
			<button
				type="button"
				className={styles.paths_bar}
				aria-expanded={open}
				onClick={() => setOpen((value) => !value)}
			>
				<span className={`${styles.chevron} ${open ? styles.chevron_down : styles.chevron_up}`} />
				<h3 className={styles.paths_title}>Journey paths</h3>
				{paths && (
					<>
						<span className={styles.paths_count}>
							{count} {count === 1 ? 'path' : 'paths'}
						</span>
						<span className={styles.paths_hint}>for the customers in view</span>
					</>
				)}
				<span className={styles.paths_toggle}>{open ? 'Hide' : 'Show'}</span>
			</button>
			{open && (
				<div className={`${styles.paths_body} ${loading ? styles.loading : ''}`}>
					{paths && count === 0 && <div className={styles.paths_empty}>{NO_PATHS}</div>}
					{count > 0 && (
						<div className={styles.paths_head}>
							<span className={styles.path_rank} />
							<span className={styles.paths_head_journey}>Journey</span>
							<span className={styles.path_customers}>Customers</span>
							<span className={styles.path_goal}>{compare ? 'Reached A · B' : 'Reached goal'}</span>
							<span className={styles.path_days}>Median time to goal</span>
							<span className={styles.paths_arrow} />
						</div>
					)}
					{paths?.map((path, index) => {
						const isOpen = expanded === path.path_key;
						const pair = compare ? pathPair(compare, path.path_key) : null;
						return (
							<div key={path.path_key}>
								<button
									type="button"
									className={`${styles.path_row} ${isOpen ? styles.path_row_open : ''}`}
									aria-expanded={isOpen}
									onClick={() => setExpanded(isOpen ? null : path.path_key)}
								>
									<span className={styles.path_rank}>{index + 1}</span>
									<PathChips steps={path.steps} lookup={lookup} />
									<span className={`${styles.path_customers} ${styles.path_value}`}>
										{formatNumber(path.customers)}
									</span>
									{pair ? (
										<span className={`${styles.path_goal} ${styles.path_value}`}>
											<span className={styles.compare_a}>{formatPct(pair[0])}</span>
											<span className={styles.compare_vs}> · </span>
											<span className={styles.compare_b}>{formatPct(pair[1])}</span>
										</span>
									) : (
										<span
											className={`${styles.path_goal} ${styles.path_value} ${styles.goal_text}`}
										>
											{path.reached_pct.toFixed(1)}%
										</span>
									)}
									<span className={`${styles.path_days} ${styles.path_value}`}>
										{path.median_secs_to_goal === null ? '' : formatSpan(path.median_secs_to_goal)}
									</span>
									<span className={styles.paths_arrow}>
										<span className={`${styles.chevron} ${isOpen ? styles.chevron_down : ''}`} />
									</span>
								</button>
								{isOpen && filters && (
									<CustomerDrilldown
										filters={filters}
										pathKey={path.path_key}
										onOpenCustomer={onOpenCustomer}
									/>
								)}
							</div>
						);
					})}
				</div>
			)}
		</section>
	);
};

export default JourneyPaths;
